import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

export interface WalletDetails {
	id: number;
	uuid: string;
	address: string;
	blockchain: string;
	type: string;
	createdAt: string;
	blockedAt?: string;
	blockedReason?: string;
	transferSuspendedAt?: string;
	transferSuspendedReason?: string;
	portfolio?: PortfolioDetails;
	transactionCounts?: TransactionCounts;
	currentLock?: WalletLockInfo;
}

export interface SuspendedWallet {
	id: number;
	uuid: string;
	address: string;
	blockchain: string;
	transferSuspendedAt: string;
	transferSuspendedReason: string;
}

export interface SuspendedWalletsResponse {
	wallets: SuspendedWallet[];
	count: number;
}

export interface PortfolioDetails {
	native?: AssetDetails;
	tokens: TokenDetails[];
	totalValueUsd: string;
	source?: string; // "database" or "blockchain"
	error?: string;
}

export interface AssetDetails {
	currency: string;
	symbol: string;
	balance: string;
	decimals: number;
	valueUsd: string;
}

export interface TokenDetails {
	currency: string;
	symbol: string;
	address: string;
	balance: string;
	decimals: number;
	valueUsd: string;
}

export interface TransactionCounts {
	mainnet?: NetworkCounts;
	testnet?: NetworkCounts;
}

export interface NetworkCounts {
	confirmed: number;
	pending: number;
}

export interface WalletLockInfo {
	merchantId: number;
	currency: string;
	networkId: string;
	lockedAt: string;
}

export interface OutboundWalletsResponse {
	wallets: WalletDetails[];
	totalValueUsd: string;
}

export interface InboundWalletsResponse {
	wallets: WalletDetails[];
	pagination: {
		totalCount: number;
		limit: number;
		offset: number;
	};
}

export interface ListInboundWalletsParams {
	limit?: number;
	offset?: number;
	blockchain?: string;
	search?: string;
}

export interface TransactionDetails {
	id: number;
	hash?: string;
	explorerLink?: string;
	type: string;
	direction: string;
	status: string;
	amount: string;
	currency: string;
	decimals: number;
	senderAddress?: string;
	recipientAddress?: string;
	networkFee?: string;
	serviceFee: string;
	usdAmount: string;
	createdAt: string;
	updatedAt: string;
	relatedOrder?: OrderReference;
}

export interface OrderReference {
	id: number;
	uuid: string;
	merchantOrderId: string;
	status: string;
}

export interface WalletTransactionsResponse {
	transactions: TransactionDetails[];
	pagination: {
		totalCount: number;
		limit: number;
		offset: number;
	};
}

export interface ListWalletTransactionsParams {
	limit?: number;
	offset?: number;
	type?: string;
	currency?: string;
}

export interface DriftResult {
	currency: string;
	networkId: string;
	dbAmount: string;
	chainAmount: string;
	diff: string;
	hasDrift: boolean;
	balanceId: number;
	error?: string;
}

export interface CheckDriftResponse {
	walletId: number;
	address: string;
	blockchain: string;
	hasDrift: boolean;
	results: DriftResult[];
}

export interface AdjustmentResult {
	currency: string;
	operation: string;
	amount: string;
	dbBefore: string;
	dbAfter: string;
}

export interface ReconcileAndResumeResponse {
	walletId: number;
	address: string;
	blockchain: string;
	adjustments: AdjustmentResult[];
	walletStatus: string;
}

export interface WalletBackupResponse {
	id: number;
	uuid: string;
	address: string;
	publicKey: string;
	privateKey: string;
	blockchain: string;
	type: string;
	createdAt: string;
}

const walletProvider = {
	async listOutboundWallets(): Promise<OutboundWalletsResponse> {
		const response = await apiRequest.get(withApiPath("/wallets/outbound"));
		return response.data;
	},

	async listInboundWallets(
		params: ListInboundWalletsParams = {},
	): Promise<InboundWalletsResponse> {
		const response = await apiRequest.get(withApiPath("/wallets/inbound"), {
			params: {
				limit: params.limit ?? 20,
				offset: params.offset ?? 0,
				blockchain: params.blockchain ?? "",
				search: params.search ?? "",
			},
		});
		return response.data;
	},

	async getWalletDetails(walletId: number): Promise<WalletDetails> {
		const response = await apiRequest.get(withApiPath(`/wallets/${walletId}`));
		return response.data;
	},

	async getWalletTransactions(
		walletId: number,
		params: ListWalletTransactionsParams = {},
	): Promise<WalletTransactionsResponse> {
		const response = await apiRequest.get(
			withApiPath(`/wallets/${walletId}/transactions`),
			{
				params: {
					limit: params.limit ?? 20,
					offset: params.offset ?? 0,
					type: params.type ?? "",
					currency: params.currency ?? "",
				},
			},
		);
		return response.data;
	},

	async downloadWalletBackup(
		walletId: number,
		twoFactorCode: string,
	): Promise<WalletBackupResponse> {
		const response = await apiRequest.get(
			withApiPath(`/wallets/${walletId}/backup`),
			{
				headers: {
					"X-2FA-Code": twoFactorCode,
				},
			},
		);
		return response.data;
	},

	async getWalletPortfolio(walletId: number): Promise<PortfolioDetails> {
		const response = await apiRequest.get(
			withApiPath(`/wallets/${walletId}/portfolio`),
		);
		return response.data;
	},

	async refreshWalletPortfolio(walletId: number): Promise<PortfolioDetails> {
		const response = await apiRequest.post(
			withApiPath(`/wallets/${walletId}/portfolio/refresh`),
		);
		return response.data;
	},

	async listSuspendedWallets(limit = 100): Promise<SuspendedWalletsResponse> {
		const response = await apiRequest.get(withApiPath("/wallets/suspended"), {
			params: { limit },
		});
		return response.data;
	},

	async suspendWalletTransfers(
		walletId: number,
		reason?: string,
	): Promise<{ wallet_id: number; status: string; reason: string }> {
		const response = await apiRequest.post(
			withApiPath(`/wallets/${walletId}/suspend-transfers`),
			{ reason: reason ?? "manually suspended by admin" },
		);
		return response.data;
	},

	async unsuspendWalletTransfers(
		walletId: number,
	): Promise<{ wallet_id: number; status: string }> {
		const response = await apiRequest.post(
			withApiPath(`/wallets/${walletId}/unsuspend-transfers`),
		);
		return response.data;
	},

	async checkBalanceDrift(walletId: number): Promise<CheckDriftResponse> {
		const response = await apiRequest.post(
			withApiPath(`/wallets/${walletId}/check-balance-drift`),
		);
		return response.data;
	},

	async reconcileAndResume(
		walletId: number,
	): Promise<ReconcileAndResumeResponse> {
		const response = await apiRequest.post(
			withApiPath(`/wallets/${walletId}/reconcile-and-resume`),
		);
		return response.data;
	},
};

export default walletProvider;
